(() => {
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  const getCountDecimals = (rawValue) => {
    const normalized = String(rawValue || '').trim().replace(',', '.');
    const decimalPart = normalized.includes('.') ? normalized.split('.')[1] : '';
    return Math.min(3, Math.max(0, decimalPart.length));
  };

  const formatCount = (value, decimals = 0, suffix = '') => {
    const formatted = Number(value || 0).toLocaleString('es-ES', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    return `${formatted}${suffix || ''}`;
  };

  const setHeroVars = (sticky, progress) => {
    const vw = window.innerWidth || 1440;
    const mobile = vw < 750;
    const tablet = vw >= 750 && vw < 1024;

    const easeOutCubic = (t) => 1 - Math.pow(1 - clamp(t), 3);
    const easeInOut = (t) => {
      t = clamp(t);
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    // V10: real fullscreen overscan. The media grows into an oversized full-screen
    // layer and stays centred, so it cannot leave the lower black band.
    const expand = easeInOut(progress / (mobile ? 0.34 : 0.28));
    const titleOut = easeOutCubic(progress / (mobile ? 0.16 : 0.12));

    const startW = mobile ? 92 : tablet ? 82 : 72;
    const startH = mobile ? 48 : tablet ? 50 : 50;
    const startTop = mobile ? 70 : tablet ? 69 : 70;

    // Overscan at the end removes any browser/Shopify preview rounding gap.
    const endW = mobile ? 112 : 108;
    const endH = mobile ? 112 : 112;
    const endTop = 50;

    const frameW = startW + (endW - startW) * expand;
    const frameH = startH + (endH - startH) * expand;
    const frameTop = startTop + (endTop - startTop) * expand;
    const radius = (mobile ? 30 : 42) * (1 - expand);

    sticky.style.setProperty('--lb-expand-progress', expand.toFixed(5));
    sticky.style.setProperty('--lb-bg-op', (0.40 - expand * 0.34).toFixed(5));
    sticky.style.setProperty('--lb-bg-scale', (1.06 - expand * 0.02).toFixed(5));
    sticky.style.setProperty('--lb-frame-w', `${frameW.toFixed(3)}vw`);
    sticky.style.setProperty('--lb-frame-h', `${frameH.toFixed(3)}svh`);
    sticky.style.setProperty('--lb-frame-top', `${frameTop.toFixed(3)}%`);
    sticky.style.setProperty('--lb-media-radius', `${Math.max(0, radius).toFixed(2)}px`);
    sticky.style.setProperty('--lb-frame-border-op', Math.max(0, 1 - expand * 1.35).toFixed(5));
    sticky.style.setProperty('--lb-title-op', Math.max(0, 1 - titleOut).toFixed(5));
    sticky.style.setProperty('--lb-title-y', `${(-78 * titleOut).toFixed(2)}px`);
    sticky.style.setProperty('--lb-title-scale', (1 - titleOut * 0.04).toFixed(5));
    sticky.style.setProperty('--lb-frame-shadow', (0.92 - expand * 0.90).toFixed(5));
    sticky.style.setProperty('--lb-progress-w', `${Math.round(expand * 100)}%`);
    sticky.style.setProperty('--lb-footer-op', '0');
    sticky.style.setProperty('--lb-video-brightness', (0.60 + expand * 0.18).toFixed(5));
    sticky.style.setProperty('--lb-video-inner-scale', (1.018 - expand * 0.008).toFixed(5));
    sticky.style.setProperty('--lb-hero-shade-op', (0.56 + expand * 0.12).toFixed(5));
  };

  const initCommunity = (root) => {
    if (!root || root.dataset.lbCommunityReady === 'true') return;
    root.dataset.lbCommunityReady = 'true';
    document.documentElement.classList.add('labosch-community-active');

    const syncCommunityTheme = (theme) => {
      const value = theme === 'light' ? 'light' : 'dark';
      root.setAttribute('data-lbx-theme', value);
      root.classList.toggle('lb-community--light', value === 'light');
      root.classList.toggle('lb-community--dark', value !== 'light');
    };

    const getCurrentTheme = () => (
      document.documentElement.getAttribute('data-lbx-color-scheme') ||
      document.body?.getAttribute('data-lbx-color-scheme') ||
      document.documentElement.getAttribute('data-theme') ||
      document.body?.getAttribute('data-theme') ||
      document.documentElement.getAttribute('color-scheme') ||
      document.body?.classList?.contains('theme-light') && 'light' ||
      document.documentElement?.classList?.contains('theme-light') && 'light' ||
      'dark'
    );

    const setDocumentTheme = (theme) => {
      const value = theme === 'light' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-lbx-color-scheme', value);
      document.documentElement.setAttribute('data-theme', value);
      document.documentElement.style.colorScheme = value;
      document.documentElement.classList.toggle('theme-light', value === 'light');
      document.documentElement.classList.toggle('theme-dark', value !== 'light');
      if (document.body) {
        document.body.setAttribute('data-lbx-color-scheme', value);
        document.body.setAttribute('data-theme', value);
        document.body.style.colorScheme = value;
        document.body.classList.toggle('theme-light', value === 'light');
        document.body.classList.toggle('theme-dark', value !== 'light');
      }
    };

    // Page default: this community page opens in dark mode even if another
    // template was left in light mode. After load, the global switch can still
    // change the page through the normal data-lbx-color-scheme mutation.
    const applyCommunityDefaultDark = () => {
      setDocumentTheme('dark');
      syncCommunityTheme('dark');
    };
    applyCommunityDefaultDark();
    window.setTimeout(applyCommunityDefaultDark, 80);
    window.setTimeout(applyCommunityDefaultDark, 260);

    syncCommunityTheme(getCurrentTheme());

    if (root.dataset.lbThemeListenerReady !== 'true') {
      root.dataset.lbThemeListenerReady = 'true';
      document.addEventListener('labosch:theme-change', (event) => {
        syncCommunityTheme(event?.detail?.theme || getCurrentTheme());
      });

      const themeObserver = new MutationObserver(() => syncCommunityTheme(getCurrentTheme()));
      themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-lbx-color-scheme', 'data-theme', 'color-scheme', 'class'] });
      if (document.body) themeObserver.observe(document.body, { attributes: true, attributeFilter: ['data-lbx-color-scheme', 'data-theme', 'color-scheme', 'class'] });
    }

    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });
    root.querySelectorAll('[data-lb-reveal]').forEach((el) => revealObserver.observe(el));

    const typed = root.querySelector('[data-lb-words]');
    if (typed && typed.dataset.lbTypedReady !== 'true') {
      typed.dataset.lbTypedReady = 'true';
      const words = (typed.getAttribute('data-lb-words') || '')
        .split(',')
        .map((w) => w.trim())
        .filter(Boolean);
      let wordIndex = 0;
      let charIndex = 0;
      let deleting = false;
      let timer = null;
      const render = () => {
        if (!words.length) return;
        const word = words[wordIndex % words.length];
        typed.textContent = word.slice(0, Math.max(0, charIndex));
        if (!deleting) {
          charIndex += 1;
          if (charIndex > word.length) {
            deleting = true;
            timer = window.setTimeout(render, 1120);
            return;
          }
        } else {
          charIndex -= 1;
          if (charIndex < 0) {
            deleting = false;
            wordIndex += 1;
            charIndex = 0;
          }
        }
        timer = window.setTimeout(render, deleting ? 36 : 64);
      };
      render();
      root._lbTypedTimer = timer;
    }

    const hero = root.querySelector('[data-lb-expand-hero]');
    const sticky = hero?.querySelector('.lb-community-expand__sticky');
    let heroTarget = 0;
    let heroCurrent = 0;
    let heroRaf = null;

    const readHeroTarget = () => {
      if (!hero || !sticky) return 0;
      const rect = hero.getBoundingClientRect();
      const vh = window.innerHeight || 820;
      const travel = Math.max(1, rect.height - vh);
      // Use the full hero scroll distance. The video reaches fullscreen inside
      // setHeroVars and then holds, instead of continuing as a half-height strip.
      return clamp(-rect.top / travel, 0, 1);
    };

    const renderHero = () => {
      heroRaf = null;
      if (!hero || !sticky) return;
      heroTarget = readHeroTarget();
      const delta = heroTarget - heroCurrent;
      heroCurrent += delta * 0.24;
      if (Math.abs(delta) < 0.0015) heroCurrent = heroTarget;
      setHeroVars(sticky, heroCurrent);
      if (heroCurrent !== heroTarget) heroRaf = requestAnimationFrame(renderHero);
    };

    const requestHeroUpdate = () => {
      if (heroRaf) return;
      heroRaf = requestAnimationFrame(renderHero);
    };

    heroCurrent = readHeroTarget();
    heroTarget = heroCurrent;
    setHeroVars(sticky, heroCurrent);
    window.addEventListener('scroll', requestHeroUpdate, { passive: true });
    window.addEventListener('resize', () => {
      heroCurrent = readHeroTarget();
      heroTarget = heroCurrent;
      setHeroVars(sticky, heroCurrent);
    }, { passive: true });

    const mediaObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const media = entry.target;
        if (media.tagName.toLowerCase() !== 'video') return;
        if (entry.isIntersecting) media.play().catch(() => {});
        else media.pause();
      });
    }, { threshold: 0.22, rootMargin: '120px 0px' });
    root.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('playsinline', '');
      if (!video.hasAttribute('preload')) video.preload = 'metadata';
      mediaObserver.observe(video);
    });

    const countObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        if (el.dataset.lbCountDone === 'true') return;
        el.dataset.lbCountDone = 'true';
        const rawTarget = el.getAttribute('data-lb-count') || '0';
        const target = Number(String(rawTarget).trim().replace(',', '.')) || 0;
        const suffix = el.getAttribute('data-lb-count-suffix') || '';
        const decimals = getCountDecimals(rawTarget);
        const duration = 3000;
        const start = performance.now();
        const step = (now) => {
          const progress = clamp((now - start) / duration, 0, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          el.textContent = formatCount(target * eased, decimals, suffix);
          if (progress < 1) requestAnimationFrame(step);
          else el.textContent = formatCount(target, decimals, suffix);
        };
        requestAnimationFrame(step);
        countObserver.unobserve(el);
      });
    }, { threshold: 0.35 });
    root.querySelectorAll('[data-lb-count]').forEach((el) => countObserver.observe(el));

    root.querySelectorAll('[data-lb-coverflow]').forEach((carousel) => {
      if (carousel.matches('[data-lb-tiktok-reels]') || carousel.closest('[data-lb-tiktok-reels]')) return;
      const items = Array.from(carousel.querySelectorAll('[data-lb-coverflow-item]')).slice(0, 3);
      const prev = carousel.querySelector('[data-lb-coverflow-prev]');
      const next = carousel.querySelector('[data-lb-coverflow-next]');
      const indexEl = carousel.querySelector('[data-lb-coverflow-index]');
      if (!items.length) return;

      carousel.setAttribute('data-lb-static-focus', 'true');
      let focusIndex = items.length >= 3 ? 1 : 0;
      let startX = 0;
      let dragging = false;

      const render = () => {
        items.forEach((item, i) => {
          item.classList.remove('is-active', 'is-prev', 'is-next', 'is-far-prev', 'is-far-next', 'is-focus');
          if (i === focusIndex) item.classList.add('is-focus');
          if (i === 0) item.classList.add('is-prev');
          if (i === 1) item.classList.add('is-active');
          if (i === 2) item.classList.add('is-next');
        });
        if (indexEl) indexEl.textContent = String(focusIndex + 1).padStart(2, '0');
      };

      const setFocus = (nextIndex) => {
        focusIndex = (nextIndex + items.length) % items.length;
        render();
      };

      prev?.addEventListener('click', () => setFocus(focusIndex - 1));
      next?.addEventListener('click', () => setFocus(focusIndex + 1));

      items.forEach((item, itemIndex) => {
        item.addEventListener('mouseenter', () => {
          if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
          carousel.classList.add('is-hovering');
          items.forEach((candidate) => candidate.classList.remove('is-focus'));
          item.classList.add('is-focus');
          // Hover is visual only: it does not change the stored active index or positions.
          if (indexEl) indexEl.textContent = String(itemIndex + 1).padStart(2, '0');
        });
        item.addEventListener('mouseleave', () => {
          if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
          carousel.classList.remove('is-hovering');
          items.forEach((candidate) => candidate.classList.remove('is-focus'));
          items[focusIndex]?.classList.add('is-focus');
          if (indexEl) indexEl.textContent = String(focusIndex + 1).padStart(2, '0');
        });
        item.addEventListener('focusin', () => {
          items.forEach((candidate) => candidate.classList.remove('is-focus'));
          item.classList.add('is-focus');
          if (indexEl) indexEl.textContent = String(itemIndex + 1).padStart(2, '0');
        });
      });

      carousel.addEventListener('pointerdown', (event) => {
        startX = event.clientX;
        dragging = false;
      });
      carousel.addEventListener('pointermove', (event) => {
        if (Math.abs(event.clientX - startX) > 8) dragging = true;
      });
      carousel.addEventListener('pointerup', (event) => {
        const delta = event.clientX - startX;
        if (Math.abs(delta) > 42) setFocus(focusIndex + (delta < 0 ? 1 : -1));
        window.setTimeout(() => { dragging = false; }, 80);
      });
      carousel._lbIsDragging = () => dragging;
      render();
    });

    const modal = root.querySelector('[data-lb-modal]');
    const modalMedia = root.querySelector('[data-lb-modal-media]');
    const modalTitle = root.querySelector('[data-lb-modal-title]');
    const modalDesc = root.querySelector('[data-lb-modal-desc]');
    const modalClose = root.querySelector('[data-lb-modal-close]');

    const closeModal = () => {
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (modalMedia) modalMedia.innerHTML = '';
    };

    const openModal = (item) => {
      if (!modal || !modalMedia) return;
      const media = item.querySelector('.lb-js-media');
      if (!media) return;
      modalTitle && (modalTitle.textContent = item.getAttribute('data-title') || '');
      modalDesc && (modalDesc.textContent = item.getAttribute('data-desc') || '');
      modalMedia.innerHTML = '';
      if (media.tagName.toLowerCase() === 'video') {
        const src = media.querySelector('source')?.src || media.currentSrc || media.src;
        const video = document.createElement('video');
        video.src = src;
        video.controls = true;
        video.autoplay = true;
        video.loop = true;
        video.playsInline = true;
        video.poster = media.poster || '';
        modalMedia.appendChild(video);
      } else if (media.tagName.toLowerCase() === 'img') {
        const img = document.createElement('img');
        img.src = media.currentSrc || media.src;
        img.alt = media.alt || '';
        modalMedia.appendChild(img);
      } else {
        modalMedia.innerHTML = media.outerHTML;
      }
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    root.querySelectorAll('[data-lb-modal-item]').forEach((item) => {
      item.addEventListener('click', (event) => {
        if (item.closest('[data-lb-tiktok-reels]')) return;
        if (event.target.closest('[data-lb-coverflow-prev], [data-lb-coverflow-next]')) return;
        const carousel = event.target.closest('[data-lb-coverflow]');
        if (carousel && carousel._lbIsDragging && carousel._lbIsDragging()) return;
        openModal(item);
      });
    });
    modalClose?.addEventListener('click', closeModal);
    modal?.addEventListener('click', (event) => { if (event.target === modal) closeModal(); });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeModal(); });
  };

  const boot = () => document.querySelectorAll('[data-lb-community]').forEach(initCommunity);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();

/* V12: robust hover focus for the 3 social cards.
   Uses pointer position over the carousel, not mouseenter on each card, so overlapped cards can still be selected. */
(() => {
  const finePointer = () => window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const setupCoverflowPointerFocus = () => {
    document.querySelectorAll('[data-lb-coverflow][data-lb-static-focus="true"]').forEach((carousel) => {
      if (carousel.matches('[data-lb-tiktok-reels]') || carousel.closest('[data-lb-tiktok-reels]')) return;
      if (carousel.dataset.lbPointerFocusReady === 'true') return;
      carousel.dataset.lbPointerFocusReady = 'true';

      const items = Array.from(carousel.querySelectorAll('[data-lb-coverflow-item]')).slice(0, 3);
      const indexEl = carousel.querySelector('[data-lb-coverflow-index]');
      if (items.length < 2) return;

      let activeIndex = items.length >= 3 ? 1 : 0;
      let raf = null;
      let lastClientX = 0;

      const apply = (index) => {
        activeIndex = Math.max(0, Math.min(items.length - 1, index));
        carousel.setAttribute('data-lb-hover-index', String(activeIndex));
        items.forEach((item, i) => {
          item.classList.toggle('is-focus', i === activeIndex);
        });
        if (indexEl) indexEl.textContent = String(activeIndex + 1).padStart(2, '0');
      };

      const indexFromPointer = (clientX) => {
        const rect = carousel.getBoundingClientRect();
        const x = (clientX - rect.left) / Math.max(1, rect.width);
        if (x < 0.40) return 0;
        if (x > 0.60) return Math.min(2, items.length - 1);
        return Math.min(1, items.length - 1);
      };

      const scheduleApply = () => {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = null;
          apply(indexFromPointer(lastClientX));
        });
      };

      carousel.addEventListener('pointerenter', (event) => {
        if (!finePointer()) return;
        lastClientX = event.clientX;
        scheduleApply();
      }, { passive: true });

      carousel.addEventListener('pointermove', (event) => {
        if (!finePointer()) return;
        lastClientX = event.clientX;
        scheduleApply();
      }, { passive: true });

      carousel.addEventListener('pointerleave', () => {
        if (!finePointer()) return;
        apply(1 < items.length ? 1 : 0);
      }, { passive: true });

      // Buttons still work, but only change visual focus; positions never swap.
      carousel.querySelector('[data-lb-coverflow-prev]')?.addEventListener('click', (event) => {
        if (!finePointer()) return;
        event.stopImmediatePropagation();
        apply(activeIndex <= 0 ? items.length - 1 : activeIndex - 1);
      }, true);

      carousel.querySelector('[data-lb-coverflow-next]')?.addEventListener('click', (event) => {
        if (!finePointer()) return;
        event.stopImmediatePropagation();
        apply(activeIndex >= items.length - 1 ? 0 : activeIndex + 1);
      }, true);

      // Initial state: centre card in front.
      apply(1 < items.length ? 1 : 0);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupCoverflowPointerFocus);
  else setupCoverflowPointerFocus();
  document.addEventListener('shopify:section:load', setupCoverflowPointerFocus);
})();

/* V13: TikTok reel focus. The visual focus is controlled by invisible hit zones,
   so overlapped cards can be selected without moving their horizontal position. */
(() => {
  const finePointer = () => window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const setupTikTokReels = () => {
    document.querySelectorAll('[data-lb-tiktok-reels]').forEach((section) => {
      if (section.dataset.lbTiktokReelsReady === 'true') return;
      section.dataset.lbTiktokReelsReady = 'true';

      const stage = section.querySelector('[data-lb-coverflow-stage]');
      const carousel = section.matches('[data-lb-coverflow]') ? section : section.closest('[data-lb-coverflow]');
      const items = Array.from(section.querySelectorAll('[data-lb-coverflow-item]')).slice(0, 3);
      const hits = Array.from(section.querySelectorAll('[data-lb-coverflow-hit]'));
      const indexEl = section.querySelector('[data-lb-coverflow-index]');
      const prev = section.querySelector('[data-lb-coverflow-prev]');
      const next = section.querySelector('[data-lb-coverflow-next]');
      if (!stage || !items.length) return;

      let active = -1;
      let pendingActive = null;
      let activeRaf = null;

      const getVideo = (item) => item?.querySelector('video');

      const syncVideos = () => {
        items.forEach((item, index) => {
          const video = getVideo(item);
          if (!video) return;
          video.muted = true;
          video.loop = true;
          video.autoplay = true;
          video.playsInline = true;
          video.setAttribute('muted', '');
          video.setAttribute('loop', '');
          video.setAttribute('autoplay', '');
          video.setAttribute('playsinline', '');
          video.preload = 'metadata';
          if (active < 0 || index === active) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      };

      const clear = () => {
        if (active < 0 && !stage.hasAttribute('data-lb-hover-index')) return;
        active = -1;
        pendingActive = null;
        if (activeRaf) {
          window.cancelAnimationFrame(activeRaf);
          activeRaf = null;
        }
        stage.removeAttribute('data-lb-hover-index');
        section.removeAttribute('data-lb-active-tiktok');
        if (carousel) carousel.removeAttribute('data-lb-hover-index');
        items.forEach((item) => {
          item.classList.remove('is-focus');
          item.setAttribute('aria-hidden', 'false');
        });
        syncVideos();
      };

      const apply = (index, force = false) => {
        const nextActive = Math.max(0, Math.min(items.length - 1, Number(index) || 0));
        if (!force && nextActive === active) return;
        active = nextActive;
        stage.setAttribute('data-lb-hover-index', String(active));
        section.setAttribute('data-lb-active-tiktok', String(active));
        if (carousel) carousel.setAttribute('data-lb-hover-index', String(active));
        items.forEach((item, itemIndex) => {
          const isFocus = itemIndex === active;
          item.classList.toggle('is-focus', isFocus);
          item.setAttribute('aria-hidden', isFocus ? 'false' : 'true');
        });
        if (indexEl) indexEl.textContent = String(active + 1).padStart(2, '0');
        syncVideos();
      };

      const scheduleApply = (index) => {
        pendingActive = Math.max(0, Math.min(items.length - 1, Number(index) || 0));
        if (pendingActive === active) return;
        if (activeRaf) return;
        activeRaf = window.requestAnimationFrame(() => {
          activeRaf = null;
          apply(pendingActive);
        });
      };

      hits.forEach((hit) => {
        const index = Number(hit.getAttribute('data-lb-coverflow-hit') || 0);
        hit.addEventListener('pointerenter', () => finePointer() && scheduleApply(index), { passive: true });
        hit.addEventListener('pointermove', () => finePointer() && scheduleApply(index), { passive: true });
        hit.addEventListener('focus', () => apply(index, true));
        hit.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
        });
      });

      stage.addEventListener('pointerleave', () => {
        if (!finePointer()) return;
        clear();
        hits.forEach((candidate) => { candidate.dataset.lbFocusedOnce = 'false'; });
      }, { passive: true });

      prev?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        apply(active <= 0 ? items.length - 1 : active - 1);
      }, true);

      next?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        apply(active >= items.length - 1 ? 0 : active + 1);
      }, true);

      // Idle state: no card is selected, so the three TikTok cards remain visible.
      clear();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupTikTokReels);
  else setupTikTokReels();
  document.addEventListener('shopify:section:load', setupTikTokReels);
})();
